import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Database, ShieldCheck, Plus, Trash2, Save, X, Search, Cpu } from "lucide-react";
import { ECM_DB, lookupEcm } from "../flashtools/lib/ecmDatabase";
import { BasemapReleasePanel } from "../components/BasemapReleasePanel";

const norm = (s) => (s || "").toUpperCase().replace(/[^0-9A-F]/g, "");

const EMPTY = { part_number: "", ecm_id: "", ecu_type: "Shindengen", mcu_type: "", model: "", smart_key: "", region: "", displacement: "", year: "", flash_size: "", start_offset: "Auto", checksum_offset: "Auto" };
const ECU_TYPES = ["Shindengen", "Keihin", "Denso", "Mitsubishi", "Bosch", "Other"];
const MAKERS = ["Shindengen", "Keihin", "Denso"];
const TAB_ACTIVE = {
  All: "bg-slate-200 text-slate-950 border-slate-200",
  Shindengen: "bg-blue-500 text-white border-blue-500",
  Keihin: "bg-red-500 text-white border-red-500",
  Denso: "bg-emerald-500 text-slate-950 border-emerald-500",
};

export default function EcuDatabase() {
  const { isAdmin, loading, login } = useAuth();
  const navigate = useNavigate();
  const [backendRows, setBackendRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [maker, setMaker] = useState("All");

  const load = useCallback(async () => {
    try { setBackendRows((await api.get("/ecu-database")).data || []); } catch { setBackendRows([]); }
  }, []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  // Built-in Flash Tools catalog mapped to the admin row shape.
  const builtinRows = useMemo(() => Object.keys(ECM_DB).map((id) => {
    const e = ECM_DB[id];
    const info = lookupEcm(id);
    return {
      id: `builtin-${id}`, _bid: null,
      part_number: e.part || info.part || "", ecm_id: id, ecu_type: e.maker || info.maker || "Other",
      mcu_type: e.mcu || info.mcu || "", model: e.model || e.name || "", smart_key: "", region: "", displacement: "", year: "",
      flash_size: e.flash || "", start_offset: (e.start ?? "") === "" ? "Auto" : e.start, checksum_offset: (e.checksum ?? "") === "" ? "Auto" : e.checksum,
    };
  }), []);

  // Merge: built-in catalog + admin records. An admin record (same ECM ID) OVERRIDES the built-in one,
  // so what you edit here is exactly what /database (ECU database) shows — they stay in sync.
  const rows = useMemo(() => {
    const map = {};
    builtinRows.forEach((r) => { map[norm(r.ecm_id)] = r; });
    backendRows.forEach((r) => { const k = norm(r.ecm_id); if (k) map[k] = { ...r, _bid: r.id }; });
    return Object.values(map).sort((a, b) =>
      (a.ecu_type || "").localeCompare(b.ecu_type || "") || (a.part_number || "").localeCompare(b.part_number || ""));
  }, [builtinRows, backendRows]);

  const makerTabs = useMemo(() => {
    const extra = [...new Set(rows.map((r) => r.ecu_type).filter(Boolean))].filter((t) => !MAKERS.includes(t));
    return ["All", ...MAKERS, ...extra];
  }, [rows]);
  const makerCount = useMemo(() => {
    const c = {};
    rows.forEach((r) => { c[r.ecu_type] = (c[r.ecu_type] || 0) + 1; });
    return c;
  }, [rows]);

  if (!loading && !isAdmin) {
    return (
      <div className="p-8 max-w-md mx-auto text-center mt-16">
        <div className="h-14 w-14 rounded-xl bg-red-500/15 border border-red-700/40 flex items-center justify-center mx-auto mb-4">
          <ShieldCheck className="h-7 w-7 text-cyan-300" />
        </div>
        <h1 className="font-head font-black text-3xl uppercase text-white">Admin Only</h1>
        <p className="text-slate-500 text-sm mt-2">Sign in with the admin Google account to manage the ECU database.</p>
        <div className="flex gap-3 justify-center mt-5">
          <Button data-testid="ecudb-login-btn" onClick={login} className="bg-gradient-to-r from-cyan-400 to-teal-500 text-slate-950 font-bold">Admin Login</Button>
          <Button variant="ghost" onClick={() => navigate("/database")} className="text-slate-400 hover:text-white hover:bg-slate-800">Back</Button>
        </div>
      </div>
    );
  }

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const resetForm = () => { setForm(EMPTY); setEditId(null); };

  const save = async () => {
    if (!form.ecm_id.trim() || !form.part_number.trim()) { toast.error("ECM/Chip ID and Part Number are required"); return; }
    const ecm = norm(form.ecm_id);
    const payload = { ...form, ecm_id: ecm };
    // Upsert by ECM ID: if a backend record already exists for this ID, update it; otherwise create one
    // (so editing a built-in catalog entry creates a persistent override that /database also shows).
    const existing = backendRows.find((b) => norm(b.ecm_id) === ecm);
    try {
      if (existing) { await api.put(`/ecu-database/${existing.id}`, payload); toast.success("ECU updated"); }
      else { await api.post("/ecu-database", payload); toast.success("ECU saved"); }
      resetForm(); load();
    } catch { toast.error("Save failed (admin only)"); }
  };

  const edit = (r) => {
    setForm({
      part_number: r.part_number || "", ecm_id: r.ecm_id || "", ecu_type: r.ecu_type || "Other",
      mcu_type: r.mcu_type || "", model: r.model || "", smart_key: r.smart_key || "", region: r.region || "", displacement: r.displacement || "", year: r.year || "",
      flash_size: r.flash_size || "", start_offset: r.start_offset || "Auto", checksum_offset: r.checksum_offset || "Auto",
    });
    setEditId(r._bid || null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (r) => {
    const existing = backendRows.find((b) => norm(b.ecm_id) === norm(r.ecm_id));
    if (!existing) { toast.info("Built-in catalog entry — edit it to create an override (built-ins can't be deleted)."); return; }
    if (!window.confirm("Delete this ECU record?")) return;
    try { await api.delete(`/ecu-database/${existing.id}`); toast.success("Deleted"); if (editId === existing.id) resetForm(); load(); }
    catch { toast.error("Delete failed"); }
  };

  const filtered = rows.filter((r) => {
    const matchMaker = maker === "All" || (r.ecu_type || "") === maker;
    const s = (q || "").toLowerCase();
    const matchQ = !s || [r.part_number, r.ecm_id, r.ecu_type, r.mcu_type].some((v) => (v || "").toLowerCase().includes(s));
    return matchMaker && matchQ;
  });

  const inputCls = "bg-[#121824] border-slate-700 text-slate-100 focus-visible:ring-cyan-400 font-mono-tt text-sm";

  return (
    <div className="p-4 sm:p-8 max-w-[1200px] mx-auto" data-testid="ecu-database-page">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><Database className="h-4 w-4" /> Admin Console</div>
        <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Update Info Bike</h1>
        <p className="text-slate-500 text-sm mt-1">Add ECU records here and they auto-resolve inside Flash Tools when the ECM/Chip ID is read (Part Number, ECU Type, MCU, offsets).</p>
      </div>

      <BasemapReleasePanel refreshKey={backendRows.length} title="Release Info Bike" button="Release Info Bike" />

      {/* Form */}
      <div data-testid="ecudb-form" className="rounded-xl border border-slate-800 bg-[#0c1017] p-5 mb-8">
        <h2 className="font-head font-bold text-xl uppercase text-white flex items-center gap-2 mb-4">
          <Cpu className="h-5 w-5 text-cyan-400" /> {editId ? "Edit ECU" : "Add ECU"}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400">Part Number</Label>
            <Input data-testid="ecudb-part-number" value={form.part_number} onChange={(e) => setF("part_number", e.target.value)} placeholder="30400-K1AA-N13" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400">ECM / Chip ID</Label>
            <Input data-testid="ecudb-ecm-id" value={form.ecm_id} onChange={(e) => setF("ecm_id", e.target.value.toUpperCase().replace(/[^0-9A-F]/g, ""))} placeholder="0103790F03" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400">ECU Type</Label>
            <select data-testid="ecudb-ecu-type" value={form.ecu_type} onChange={(e) => setF("ecu_type", e.target.value)}
              className="w-full h-10 rounded-md bg-[#121824] border border-slate-700 text-slate-100 text-sm px-3 focus:outline-none focus:ring-1 focus:ring-cyan-400">
              {ECU_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400">MCU Type</Label>
            <Input data-testid="ecudb-mcu-type" value={form.mcu_type} onChange={(e) => setF("mcu_type", e.target.value)} placeholder="RH850 / SH7058 …" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400">Model</Label>
            <Input data-testid="ecudb-model" value={form.model} onChange={(e) => setF("model", e.target.value)} placeholder="Vario 160 / PCX …" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400">Smart Key</Label>
            <Input data-testid="ecudb-smart-key" value={form.smart_key} onChange={(e) => setF("smart_key", e.target.value)} placeholder="Yes / No" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400">Region</Label>
            <Input data-testid="ecudb-region" value={form.region} onChange={(e) => setF("region", e.target.value)} placeholder="ID / TH / JP …" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400">Displacement</Label>
            <Input data-testid="ecudb-displacement" value={form.displacement} onChange={(e) => setF("displacement", e.target.value)} placeholder="160cc …" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400">Year</Label>
            <Input data-testid="ecudb-year" value={form.year} onChange={(e) => setF("year", e.target.value)} placeholder="2022 …" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400">Flash Size (KB)</Label>
            <Input data-testid="ecudb-flash-size" value={form.flash_size} onChange={(e) => setF("flash_size", e.target.value)} placeholder="256 / 512 / 1024 …" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400">Start Offset</Label>
            <Input data-testid="ecudb-start-offset" value={form.start_offset} onChange={(e) => setF("start_offset", e.target.value)} placeholder="Auto or 0x…" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400">Checksum Offset</Label>
            <Input data-testid="ecudb-checksum-offset" value={form.checksum_offset} onChange={(e) => setF("checksum_offset", e.target.value)} placeholder="Auto or 0x…" className={inputCls} />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <Button data-testid="ecudb-save-btn" onClick={save} className="bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold">
            {editId ? <><Save className="h-4 w-4 mr-1.5" /> Update</> : <><Plus className="h-4 w-4 mr-1.5" /> Add ECU</>}
          </Button>
          {editId && (
            <Button data-testid="ecudb-cancel-btn" onClick={resetForm} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
              <X className="h-4 w-4 mr-1.5" /> Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex flex-col gap-3 mb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-head font-bold text-xl uppercase text-white flex items-center gap-2"><Database className="h-5 w-5 text-cyan-400" /> Records <span className="text-slate-600 text-sm font-mono-tt">({rows.length})</span></h2>
          <div className="relative">
            <Search className="h-4 w-4 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input data-testid="ecudb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search part / ID / type" className="bg-[#121824] border-slate-700 text-slate-100 pl-8 h-9 w-[240px] text-sm" />
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {makerTabs.map((b) => (
            <button key={b} data-testid={`ecudb-maker-filter-${b}`} onClick={() => setMaker(b)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider border transition-colors ${
                maker === b ? (TAB_ACTIVE[b] || "bg-slate-200 text-slate-950 border-slate-200") : "bg-[#0c1017] text-slate-400 border-slate-700 hover:border-slate-500"}`}>
              {b}{b !== "All" && makerCount[b] ? ` ${makerCount[b]}` : ""}
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-slate-800 overflow-hidden bg-[#0c1017]">
        <div className="overflow-auto max-h-[65vh]">
          <table className="w-full text-sm" data-testid="ecudb-table">
            <thead>
              <tr className="bg-[#121824] border-b border-slate-800">
                {["Part Number", "ECM / Chip ID", "ECU Type", "MCU Type", "Flash (KB)", "Start", "Checksum", "Actions"].map((h) => (
                  <th key={h} className="sticky top-0 bg-[#121824] text-left px-3 py-3 font-mono-tt text-[11px] uppercase tracking-wider text-slate-400 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`ecudb-row-${r.id}`} className="border-b border-slate-800/60 hover:bg-cyan-400/5">
                  <td className="px-3 py-2.5 text-slate-200 whitespace-nowrap">{r.part_number}</td>
                  <td className="px-3 py-2.5 font-mono-tt text-emerald-400 whitespace-nowrap">{r.ecm_id}</td>
                  <td className="px-3 py-2.5 text-slate-300 whitespace-nowrap">{r.ecu_type}</td>
                  <td className="px-3 py-2.5 text-slate-300 whitespace-nowrap">{r.mcu_type || "-"}</td>
                  <td className="px-3 py-2.5 font-mono-tt text-slate-400 whitespace-nowrap">{r.flash_size ? `${r.flash_size} KB` : "-"}</td>
                  <td className="px-3 py-2.5 font-mono-tt text-slate-400 whitespace-nowrap">{r.start_offset || "-"}</td>
                  <td className="px-3 py-2.5 font-mono-tt text-slate-400 whitespace-nowrap">{r.checksum_offset || "-"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="flex gap-1.5">
                      <button data-testid={`ecudb-edit-${r.id}`} onClick={() => edit(r)} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 text-xs"><Save className="h-3.5 w-3.5" /> Edit</button>
                      <button data-testid={`ecudb-delete-${r.id}`} onClick={() => del(r)} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-800 text-red-400 hover:bg-red-950/40 text-xs"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-600 font-mono-tt">No ECU records yet. Add one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
